import React, { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AuthContext, { type IAuthService, TokenState } from './AuthContext';
import envConfig from "../../helpers/envConfigHelper";
import { Auth0Client } from '@auth0/auth0-spa-js';

const OauthInnerAuthContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [tokenState, setTokenState] = useState<TokenState | undefined>(TokenState.Valid);
    const [user, setUser] = useState<any>(null);

    useEffect(() => {
        (async () => {
            const client = await getAuth0Client();
            const isAuthenticated = await client.isAuthenticated();
            if (isAuthenticated) {
                const user = await client.getUser();
                //console.log'Auth0 User:', user);
                setUser(user);
                setTokenState(TokenState.Valid);
            }
        })();
    }, []);

    const userLogin = useCallback(async (redirectPath = '/') => {
        const shouldLogin = redirectPath.startsWith('/');
        const client = await getAuth0Client();
        const isAuthenticated = await client.isAuthenticated();
        if (isAuthenticated) {
            //console.log'User is already authenticated');
            return;
        }
        //console.log'Initiating login with redirect. Redirect path:', redirectPath, 'Show sign up form:', shouldLogin);
        return client.loginWithRedirect({
            appState: { target: redirectPath },
            authorizationParams: {
                ...(shouldLogin ? { screen_hint: 'login' } : { screen_hint: 'signup' }),
                ...(false ? { signup: true } : {}),
            },
        });
    }, []);

    //Dont redirect to DIP login page - Show landing page
    // useEffect(() => {
    //     HTTPDataService.setUnauthorizedHandler(() => {
    //         setTokenState(TokenState.Invalid);
    //         void userLogin();
    //     });

    //     return () => {
    //         HTTPDataService.setUnauthorizedHandler(undefined);
    //     };
    // }, [userLogin]);

    const getAccessToken = useCallback(async () => {
        await verifyCallback();

        //return "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6Ik1ERXhORFZCUWpGRE9FWTJPVVJEUVRkQk5URkNNVFUyTlVVMVF6QXdSVEZCTWpVNE9EaEZNUSJ9.eyJodHRwczovL2RhbmZvc3MuY29tL2RhbmZvc3NJZGVudGl0eUlkIjoiVTQyMTY3MCIsImh0dHBzOi8vZGFuZm9zcy5jb20vaXN3b3JrZm9yY2UiOnRydWUsImh0dHBzOi8vZGFuZm9zcy5jb20vd29ya2ZvcmNlX3R5cGUiOiJ3b3JrZm9yY2VfaW50ZXJuYWwiLCJpc3MiOiJodHRwczovL2FjY291bnRzLWUyZS5kYW5mb3NzLmNvbS8iLCJzdWIiOiJ3YWFkfDNQRlNsS3kzTGpuR1RGc1JoVXhyYVVvN1RacXkxV3V6NEZlem9yaUltd1kiLCJhdWQiOlsiaHR0cHM6Ly9hcGkucGx1czFjbG91ZC5kYW5mb3NzLmNvbSIsImh0dHBzOi8vZGFuZm9zcy1lMmUuZXUuYXV0aDAuY29tL3VzZXJpbmZvIl0sImlhdCI6MTc4NjA4MjEzNCwiZXhwIjoxNzg2MTY4NTM0LCJzY29wZSI6Im9wZW5pZCBwcm9maWxlIGVtYWlsIiwiYXpwIjoiNDRTSGRSd1B0bkFadktIU0NURlpERGd2NU5RRGl6RGgifQ.io4i_8Pvtwen3-UZoybw0-aO3N63nuLPP-Ie8Eeca9d8ly-qSH-7vnIk0m-AuDvqoJLQ7Jp2kDS-5SgxxJ6FSRw1_9MvZZvt5xteetZIdrk3fkvoFLduW-S995c3yVVRHD449cEU6l9FuU4hgrkFmmyQy58_0cQh4m_QdBt6E_Ms4ZpSqpfXZfqeTPdntKajeqj0olumiiQVlXAY0L0lpPwMyiyI10iiLfCHRGnFGv5PjJnozSB79JjJrXb0ovz4qXHnjDowpOxeWeGHtTto_mCvo8wWk1y4motA3V_ItF3wi7YZ0O0o7Cvwt4-TM03hUESd9NczbFEREtHN9kUkyQ";
        try {
            const client = await getAuth0Client();

            // Always check fresh auth status
            const isAuthenticated = await client.isAuthenticated();

            if (!isAuthenticated) {
                setTokenState(TokenState.Invalid);
                //userLogin(); //Dont redirect to DIP login page - Show landing page
                return undefined;
            }

            // Force fresh token check occasionally
            const cacheMode = Math.random() > 0.8 ? 'off' : 'on'; // 20% of time force refresh

            const token = await client.getTokenSilently({
                cacheMode: 'off',
                timeoutInSeconds: 60
            });

            setTokenState(TokenState.Valid);
            return token;

        } catch (error) {
            console.error('Error getting access token:', error);
            if (error instanceof Error) {
                console.error('Error message:', error.message);
            }

            if (typeof error === 'object' && error !== null && 'error' in error) {
                const auth0Error = error as { error: string; error_description?: string };

                if (auth0Error.error === 'login_required') {
                    setTokenState(TokenState.Invalid);
                    userLogin();
                } else if (auth0Error.error === 'consent_required') {
                    userLogin();
                }
            }

            setTokenState(TokenState.Invalid);
            return undefined;
        }
    }, []);

    const getSalesforceToken = useCallback(async () => {
        try {
            const client = await getAuth0Client();
            const token = await client.getTokenSilently({
                authorizationParams: {
                    audience: "urn:danfoss-source-secured-keys-api",
                },
            });
            return token;
        } catch (error) {
            console.error('Error getting Salesforce token:', error);
        }
    }, []);
    const login = useCallback(async () => {
        //console.log'Login called');
        //check if the user is authenticated by checking the code in the url
        userLogin();
    }, []);

    const logout = useCallback(() => {
        //console.log'Logout called');
        getAuth0Client().then(client => {
            client.logout({
                logoutParams: {
                    returnTo: window.location.origin + '/logout',
                },
            });
            setUser(null);
            setTokenState(TokenState.Invalid);
        });
    }, []);

    const service = useMemo<IAuthService>(() => {
        return {
            login,
            logout,
            getAccessToken,
            getSalesforceToken,
            user,
            tokenState,
            isAuthenticated: tokenState === TokenState.Valid && user != null,
        };
    }, [login, logout, user, tokenState, getAccessToken, getSalesforceToken]);

    return <AuthContext service={service}>
        {children}
    </AuthContext>;

};


export const verifyCallback = async () => {

    //console.log'Handling callback');
    if (window.location.search.includes("code=")) {
        try {
            const client = await getAuth0Client();
            await client.handleRedirectCallback().then(async (result) => {
                //const user = await client.getUser();
                //console.log'Auth0 User after handling redirect callback:', user);
                const targetRoute = result.appState && result.appState.target ? result.appState.target : '/';
                window.history.replaceState({}, document.title, window.location.pathname);
                window.location.replace(targetRoute);
            }).catch((error) => {
                console.error('Error handling redirect callback:', error);

            });

            // const target = result?.appState?.target || "/";
            // window.history.replaceState({}, document.title, window.location.pathname);
            // window.location.replace(target);
        } catch (err) {
            console.error("Callback error:", err);
        }
    }

}

let auth0ClientPromise: Promise<Auth0Client> | null = null;

export function getAuth0Client() {

    if (!auth0ClientPromise) {
        auth0ClientPromise = Promise.resolve(new Auth0Client({
            domain: new URL(envConfig.get("dip-base-url")).host,
            clientId: envConfig.get("dip-client-id"),
            authorizationParams: {
                redirect_uri: `${window.location.origin}/callback`,
                scope: "openid profile email",
                audience: envConfig.get("audience")
            },
            cacheLocation: "localstorage",
        })).then(async (client) => {
            const user = await client.getUser();
            //console.log'Auth0 User context:', user);
            return client;
        });
    }

    return auth0ClientPromise;
}


export default OauthInnerAuthContextProvider;
